import React, { useState, useEffect } from "react";
import styled from "styled-components";
import axios from "axios";
import { isContentVisible } from "../utils/contentVisibility";
import YouTubeModal from "../components/YouTubeModal";
import { getReliableThumbnailUrl, handleThumbnailError } from "../utils/youtubeThumbnail";

// Set axios base URL
const configuredApiBaseUrl = process.env.REACT_APP_API_BASE_URL;
axios.defaults.baseURL = configuredApiBaseUrl?.startsWith("http://") && window.location.protocol === "https:"
    ? ""
    : configuredApiBaseUrl || (window.location.protocol === "https:" ? "" : "http://localhost:5013");

const PRContainer = styled.div`
    min-height: 100vh;
    background: white;
    padding: 4rem 0;
`;

const PRContent = styled.div`
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 20px;
`;

const PRTitle = styled.h1`
    font-size: 2.5rem;
    font-weight: 600;
    margin-bottom: 3rem;
    color: #333;
    text-align: center;

    @media (max-width: 768px) {
        font-size: 2rem;
        margin-bottom: 2rem;
    }
`;

const PRGrid = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2rem;
    margin-bottom: 4rem;
`;

const PRCard = styled.div`
    background: white;
    border: 1px solid #f0f0f0;
    border-radius: 8px;
    overflow: hidden;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
    display: flex;
    height: 200px;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    }

    @media (max-width: 768px) {
        flex-direction: column;
        height: auto;
    }
`;

const PRImage = styled.div`
    width: 300px;
    height: 200px;
    position: relative;
    overflow: hidden;

    @media (max-width: 768px) {
        width: 100%;
        height: 200px;
    }
`;

const PRThumbnail = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;

    ${PRCard}:hover & {
        transform: scale(1.05);
    }

    @media (max-width: 768px) {
        ${PRCard}:hover & {
            transform: none;
        }
    }
`;

const PlayButton = styled.div`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 50px;
    height: 50px;
    background: rgba(0, 0, 0, 0.8);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 1.2rem;
    opacity: 0;
    transition: all 0.3s ease;

    ${PRCard}:hover & {
        opacity: 1;
        transform: translate(-50%, -50%) scale(1.1);
    }

    @media (max-width: 768px) {
        width: 40px;
        height: 40px;
        font-size: 1rem;
        opacity: 0.8;

        ${PRCard}:hover & {
            opacity: 0.8;
            transform: translate(-50%, -50%);
        }
    }
`;

const PRInfo = styled.div`
    padding: 1.5rem;
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;

    @media (max-width: 768px) {
        padding: 1rem;
    }
`;

const PRName = styled.h3`
    font-size: 1.4rem;
    font-weight: 600;
    color: #333;
    margin-bottom: 0.5rem;
    line-height: 1.4;

    @media (max-width: 768px) {
        font-size: 1.2rem;
    }
`;

const PRDescription = styled.p`
    color: #666;
    line-height: 1.6;
    margin-bottom: 1rem;
    flex: 1;

    @media (max-width: 768px) {
        font-size: 0.9rem;
        margin-bottom: 0.8rem;
    }
`;

const PRCategory = styled.span`
    background: #f0f0f0;
    color: #666;
    padding: 0.3rem 0.8rem;
    border-radius: 15px;
    font-size: 0.8rem;
    font-weight: 400;

    @media (max-width: 768px) {
        padding: 0.2rem 0.6rem;
        font-size: 0.75rem;
    }
`;

interface Content {
    id: number;
    title: string;
    description: string;
    content_type: "works" | "product" | "pr";
    category: string;
    youtube_url: string;
    youtube_id: string;
    thumbnail_url: string;
    duration: string;
    published_at: string;
    view_count: number;
    like_count: number;
    custom_thumbnail: string;
    custom_description: string;
    custom_published_at: string;
    is_active: boolean;
    sort_order: number;
    created_at: string;
    updated_at: string;
}

const PR: React.FC = () => {
    const [prContent, setPrContent] = useState<Content[]>([]);
    const [selectedContent, setSelectedContent] = useState<Content | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchPRContent();
    }, []);

    const fetchPRContent = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`/api/content?type=pr`);
            if (response.data.success) {
                setPrContent((response.data.data || []).filter(isContentVisible));
            }
        } catch (error) {
            console.error("Error fetching PR content:", error);
        } finally {
            setLoading(false);
        }
    };

    const handlePRClick = (pr: Content) => {
        setSelectedContent(pr);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedContent(null);
    };

    if (loading) {
        return (
            <PRContainer>
                <PRContent>
                    <PRTitle>PR</PRTitle>
                    <div style={{ textAlign: 'center', padding: '2rem' }}>Loading...</div>
                </PRContent>
            </PRContainer>
        );
    }

    return (
        <PRContainer>
            <PRContent>
                <PRTitle>PR</PRTitle>

                {prContent.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '2rem' }}>
                        No PR content available.
                    </div>
                ) : (
                    <PRGrid>
                        {prContent.map((pr) => (
                            <PRCard key={pr.id} onClick={() => handlePRClick(pr)}>
                                <PRImage>
	                                    <PRThumbnail
	                                        src={getReliableThumbnailUrl(pr.custom_thumbnail || pr.thumbnail_url)}
	                                        alt={pr.title}
	                                        onError={(e) => handleThumbnailError(e.currentTarget)}
	                                    />
                                    <PlayButton>▶</PlayButton>
                                </PRImage>
                                <PRInfo>
                                    <PRName>{pr.title}</PRName>
                                    <PRDescription>{pr.custom_description || pr.description}</PRDescription>
                                    {/* <PRCategory>{pr.category}</PRCategory> */}
                                </PRInfo>
                            </PRCard>
                        ))}
                    </PRGrid>
                )}
            </PRContent>

            <YouTubeModal content={selectedContent} isOpen={isModalOpen} onClose={handleCloseModal} />
        </PRContainer>
    );
};

export default PR;
